import { Graphics, Sprite, Text, Texture } from "pixi.js"
import { ScreenDimensions, ScreenMessage, TileSize } from "game-common/models"
import { TextRenderer } from "../text/text_renderer"
import { Fader } from "./fader"
import { buildAmbientStripTexture } from "./ambient_strip"

// An "important" notice, rendered as a calm ambient strip: a thin translucent band near
// the top of the screen whose left/right edges fade to nothing. It fades in, holds long
// enough to read, then fades out. Deliberately NOT a hard black slab that pops in and
// shrinks away — that read as an alarm (and the shrink looked buggy). The point is to be
// noticed without blocking the game behind it.

const STRIP_TOP_Y = 40 // distance from the top of the screen to the top of the strip
const VERTICAL_PADDING = 12 // space above/below the text inside the strip
const HORIZONTAL_PADDING = 24 // keep wrapped text clear of the viewport edges
const STRIP_ALPHA = 0.5 // center opacity of the strip; low so the game stays visible behind it

export class ImportantMessage extends Graphics {
    color: number
    screenMessage: ScreenMessage
    bannerText: Text | null = null
    background: Sprite | null = null

    fadeInFader: Fader | null = null
    fadeOutFader: Fader | null = null
    holdTimer: any = null
    displayCallback: (() => void) | null = null

    // The strip spans the full visible width, which never changes (ScreenDimensions is
    // fixed and the stage is scaled to fit), so the gradient texture is built once and reused.
    private gradientTexture: Texture | null = null

    constructor(color: number = 0xffffff, screenMessage: ScreenMessage) {
        super()
        this.color = color
        this.screenMessage = screenMessage
        this.visible = false
        this.zIndex = 55000 // above the rest of the HUD; below the hero banner / countdown (60000)
    }

    // Same full width hero_message uses (the visible viewport is wider than the 640 play area).
    private stripWidth = () => ScreenDimensions.w + TileSize * 4.7

    update = (message: ScreenMessage) => {
        const { text, duration, displayCallback } = message
        this.displayCallback = displayCallback || null

        // Interrupt any in-flight fade/hold so a newer message shows cleanly.
        this.stopAnimations()
        this.removeChildren()

        const width = this.stripWidth()

        const bannerText = new TextRenderer()
            .variant("ImportantFont")
            .color(this.color)
            .align("center")
            .wordWrap(width - HORIZONTAL_PADDING * 2)
            .render(text) as Text
        this.bannerText = bannerText
        const stripHeight = bannerText.height + VERTICAL_PADDING * 2

        // Background gradient strip (built once, reused thereafter).
        if (!this.gradientTexture) {
            this.gradientTexture = buildAmbientStripTexture(width, stripHeight, STRIP_ALPHA)
        }
        const background = new Sprite(this.gradientTexture)
        background.x = 0
        background.y = STRIP_TOP_Y
        background.width = width
        background.height = stripHeight
        if (this.gradientTexture === Texture.WHITE) {
            // Fallback path (no 2D canvas): tint + alpha the flat texture so it still reads as a dark strip.
            background.tint = 0x000000
            background.alpha = STRIP_ALPHA
        }
        this.background = background
        this.addChild(background)

        // Text centered horizontally across the full width, vertically within the strip.
        bannerText.x = width / 2 - bannerText.width / 2
        bannerText.y = STRIP_TOP_Y + (stripHeight - bannerText.height) / 2
        this.addChild(bannerText)

        // Hold time scales gently with message length (min 1.5s) so there's time to read it.
        const wordCount = text.split(" ").length
        const holdMs = duration || Math.max(1500, wordCount * 300)

        // Fade in -> hold -> fade out. No pop-in, no shrink-to-zero.
        this.visible = true
        this.alpha = 0.0
        this.fadeInFader = new Fader(this, {
            direction: "in",
            minAlpha: 1.0,
            fadeInterval: 5,
            fadeAlphaDecrement: 0.025, // ~200ms fade in
            releaseCallback: () => {
                this.fadeInFader = null
                this.holdTimer = setTimeout(() => {
                    this.holdTimer = null
                    this.startFadeOut()
                }, holdMs)
            },
        })
        this.fadeInFader.update()
    }

    private startFadeOut = () => {
        this.fadeOutFader = new Fader(this, {
            direction: "out",
            minAlpha: 0.0,
            fadeInterval: 10,
            fadeAlphaDecrement: 0.025, // ~400ms fade out
            releaseCallback: () => {
                this.fadeOutFader = null
                this.visible = false
                this.alpha = 1.0
                this.removeChildren()
                if (this.displayCallback) {
                    this.displayCallback()
                    this.displayCallback = null
                }
            },
        })
        this.fadeOutFader.update()
    }

    private stopAnimations = () => {
        if (this.fadeInFader) {
            this.fadeInFader.stop(true)
            this.fadeInFader = null
        }
        if (this.fadeOutFader) {
            this.fadeOutFader.stop(true)
            this.fadeOutFader = null
        }
        if (this.holdTimer) {
            clearTimeout(this.holdTimer)
            this.holdTimer = null
        }
    }

    // Mirrors HeroMessage.destroy: tear down timers/faders so nothing fires against a dead Graphics.
    destroy = () => {
        this.stopAnimations()
        this.removeChildren()
        this.visible = false
        this.alpha = 1.0
    }
}
